import { FileText, FolderOpen, Home, ReceiptText, Settings, WalletCards } from "lucide-react";
import type React from "react";
import type { AppView, DriverProfile } from "@/types/tax";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { isPaidPlan, planLabel } from "@/lib/plans";
import { cn } from "@/lib/utils";

const navItems: Array<{ id: AppView; label: string; icon: typeof Home }> = [
  { id: "dashboard", label: "Dashboard", icon: Home },
  { id: "income", label: "Income", icon: WalletCards },
  { id: "expenses", label: "Expenses", icon: ReceiptText },
  { id: "documents", label: "Documents", icon: FolderOpen },
  { id: "settings", label: "Settings", icon: Settings },
];

interface AppShellProps {
  activeView: AppView;
  onNavigate: (view: AppView) => void;
  profile: DriverProfile;
  onLogout: () => void;
  children: React.ReactNode;
}

export function AppShell({ activeView, onNavigate, profile, onLogout, children }: AppShellProps) {
  return (
    <div className="min-h-dvh bg-background text-ink">
      <div className="lg:grid lg:grid-cols-[248px_1fr]">
        <aside className="fixed inset-y-0 left-0 z-30 hidden w-[248px] border-r border-border bg-white lg:flex lg:flex-col">
          <div className="flex h-20 items-center gap-3 px-5">
            <div className="flex size-10 items-center justify-center rounded-xl bg-primary text-primary-ink">
              <FileText className="size-5" aria-hidden="true" />
            </div>
            <div>
              <p className="font-semibold text-ink">RideShareTax</p>
              <p className="text-xs text-muted">GST workspace</p>
            </div>
          </div>
          <nav className="flex flex-1 flex-col gap-1 px-3" aria-label="Primary">
            {navItems.map((item) => (
              <NavButton
                key={item.id}
                item={item}
                active={activeView === item.id}
                onClick={() => onNavigate(item.id)}
              />
            ))}
          </nav>
          <div className="border-t border-border p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink">{profile.displayName}</p>
                <p className="text-xs text-muted">Secure account</p>
              </div>
              <Badge variant={isPaidPlan(profile.plan) ? "credit" : "outline"} className="shrink-0">
                {planLabel(profile.plan)}
              </Badge>
            </div>
            <Button type="button" variant="ghost" size="sm" className="mt-3 w-full justify-start" onClick={onLogout}>
              Log out
            </Button>
          </div>
        </aside>

        <div className="lg:col-start-2">
          <header className="sticky top-0 z-20 border-b border-border bg-white/95 px-4 py-3 backdrop-blur lg:hidden">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-xl bg-primary text-primary-ink">
                  <FileText className="size-5" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold">RideShareTax</p>
                  <div className="mt-0.5 flex min-w-0 items-center gap-2">
                    <p className="truncate text-xs text-muted">{profile.displayName}</p>
                    <Badge
                      variant={isPaidPlan(profile.plan) ? "credit" : "outline"}
                      className="min-h-5 shrink-0 px-2 text-[0.65rem]"
                    >
                      {planLabel(profile.plan)}
                    </Badge>
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="shrink-0" onClick={onLogout}>
                Log out
              </Button>
            </div>
          </header>

          <main className="mx-auto w-full max-w-[1180px] px-4 pb-[calc(5.25rem+env(safe-area-inset-bottom))] pt-5 sm:px-6 lg:px-8 lg:py-8">{children}</main>

          <nav
            className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-white/95 px-4 pb-[max(env(safe-area-inset-bottom),0.25rem)] pt-1.5 backdrop-blur lg:hidden"
            aria-label="Mobile primary"
          >
            <div className="mx-auto grid w-full max-w-[500px] grid-cols-5 gap-1.5">
              {navItems.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  className={cn(
                    "flex min-h-14 min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-1 text-[0.68rem] font-semibold text-muted transition-colors",
                    activeView === item.id && "bg-primary/10 text-primary",
                  )}
                  onClick={() => onNavigate(item.id)}
                >
                  <item.icon className="size-4" aria-hidden="true" />
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </nav>
        </div>
      </div>
    </div>
  );
}

function NavButton({
  item,
  active,
  onClick,
}: {
  item: { label: string; icon: typeof Home };
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={cn(
        "flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm font-semibold text-muted transition-colors hover:bg-surface hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
        active && "bg-primary/10 text-primary",
      )}
      onClick={onClick}
    >
      <item.icon className="size-4" aria-hidden="true" />
      {item.label}
    </button>
  );
}
